import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  X,
  Sparkles,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface CalendarPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelectDate: (dateStr: string, isVenueTbd?: boolean) => void;
  initialDate?: string;
  showVenueTbdOption?: boolean;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function formatDateToISO(year: number, month: number, day: number): string {
  const y = String(year).padStart(4, '0');
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateDisplay(isoString: string): string {
  if (!isoString) return '';
  const parts = isoString.split('-');
  if (parts.length !== 3) return isoString;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  const date = new Date(year, month, day);
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getTodayISO(): string {
  const today = new Date();
  return formatDateToISO(today.getFullYear(), today.getMonth(), today.getDate());
}

export const CalendarPickerModal: React.FC<CalendarPickerModalProps> = ({
  open,
  onClose,
  onSelectDate,
  initialDate,
  showVenueTbdOption = false,
}) => {
  const today = useMemo(() => new Date(), []);
  const todayISO = useMemo(() => getTodayISO(), []);

  // Parse initial date or default to today/tomorrow
  const initialParsed = useMemo(() => {
    if (initialDate && /^\d{4}-\d{2}-\d{2}$/.test(initialDate)) {
      const [y, m, d] = initialDate.split('-').map((num) => parseInt(num, 10));
      return { year: y, month: m - 1, day: d, iso: initialDate };
    }
    return {
      year: today.getFullYear(),
      month: today.getMonth(),
      day: today.getDate(),
      iso: todayISO,
    };
  }, [initialDate, today, todayISO]);

  const [viewYear, setViewYear] = useState<number>(initialParsed.year);
  const [viewMonth, setViewMonth] = useState<number>(initialParsed.month);
  const [selectedStartDate, setSelectedStartDate] = useState<string>(initialParsed.iso);
  const [selectedEndDate, setSelectedEndDate] = useState<string | undefined>(undefined);
  const [isMultiDay, setIsMultiDay] = useState<boolean>(false);
  const [isVenueTbd, setIsVenueTbd] = useState<boolean>(false);

  // Sync when modal opens
  useEffect(() => {
    if (open) {
      setViewYear(initialParsed.year);
      setViewMonth(initialParsed.month);
      setSelectedStartDate(initialParsed.iso);
      setSelectedEndDate(undefined);
      setIsMultiDay(false);
      setIsVenueTbd(false);
    }
  }, [open, initialParsed]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Month navigation
  const goToPreviousMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((prev) => prev - 1);
    } else {
      setViewMonth((prev) => prev - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((prev) => prev + 1);
    } else {
      setViewMonth((prev) => prev + 1);
    }
  };

  // Days in current month grid
  const daysInMonth = useMemo(() => {
    return new Date(viewYear, viewMonth + 1, 0).getDate();
  }, [viewYear, viewMonth]);

  const firstDayOfWeek = useMemo(() => {
    return new Date(viewYear, viewMonth, 1).getDay();
  }, [viewYear, viewMonth]);

  // Preset shortcuts
  const applyPreset = useCallback((preset: 'weekend' | 'nextMonth' | 'in3Months' | 'in6Months' | 'nextYear') => {
    const now = new Date();
    let target = new Date();

    if (preset === 'weekend') {
      // Find upcoming Saturday
      const dayOfWeek = now.getDay();
      const daysUntilSaturday = (6 - dayOfWeek + 7) % 7 || 7;
      target.setDate(now.getDate() + daysUntilSaturday);
    } else if (preset === 'nextMonth') {
      target = new Date(now.getFullYear(), now.getMonth() + 1, 15);
    } else if (preset === 'in3Months') {
      target = new Date(now.getFullYear(), now.getMonth() + 3, 15);
    } else if (preset === 'in6Months') {
      target = new Date(now.getFullYear(), now.getMonth() + 6, 15);
    } else if (preset === 'nextYear') {
      target = new Date(now.getFullYear() + 1, now.getMonth(), 15);
    }

    const iso = formatDateToISO(target.getFullYear(), target.getMonth(), target.getDate());
    setViewYear(target.getFullYear());
    setViewMonth(target.getMonth());
    setSelectedStartDate(iso);
    setSelectedEndDate(undefined);
  }, []);

  const handleDateClick = (day: number) => {
    const dateISO = formatDateToISO(viewYear, viewMonth, day);

    if (!isMultiDay) {
      setSelectedStartDate(dateISO);
      setSelectedEndDate(undefined);
      return;
    }

    // Multi-day selection logic
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(dateISO);
      setSelectedEndDate(undefined);
    } else {
      if (dateISO < selectedStartDate) {
        setSelectedEndDate(selectedStartDate);
        setSelectedStartDate(dateISO);
      } else if (dateISO > selectedStartDate) {
        setSelectedEndDate(dateISO);
      } else {
        // Clicked same date again, keep as start date
        setSelectedEndDate(undefined);
      }
    }
  };

  const handleConfirm = () => {
    if (!selectedStartDate) return;

    let finalDateString = selectedStartDate;
    if (isMultiDay && selectedEndDate && selectedEndDate !== selectedStartDate) {
      finalDateString = `${formatDateDisplay(selectedStartDate)} to ${formatDateDisplay(selectedEndDate)}`;
    } else {
      finalDateString = formatDateDisplay(selectedStartDate);
    }

    onSelectDate(finalDateString, isVenueTbd);
    onClose();
  };

  if (!open) return null;

  const currentYear = today.getFullYear();
  const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear + i);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="calendar-modal-title"
    >
      <div className="fixed inset-0" onClick={onClose} />
      <div
        className="relative bg-background rounded-2xl shadow-2xl border border-border w-full max-w-md overflow-hidden z-10 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border/80 flex items-center justify-between bg-muted/40">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <CalendarIcon className="h-5 w-5" />
            </div>
            <div>
              <h3 id="calendar-modal-title" className="text-base font-semibold text-foreground">
                Pick Celebration Date
              </h3>
              <p className="text-xs text-muted-foreground">
                Select the big day for your celebration
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close calendar"
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Preset chips */}
        <div className="px-5 pt-3 pb-1 flex flex-wrap gap-1.5 items-center">
          <span className="text-[11px] font-medium text-muted-foreground mr-1 flex items-center gap-1">
            <Sparkles className="h-3 w-3 text-amber-500" />
            Quick:
          </span>
          <button
            type="button"
            onClick={() => applyPreset('weekend')}
            className="px-2.5 py-1 text-xs rounded-full border border-primary/20 bg-primary/5 hover:bg-primary/15 text-foreground transition-all cursor-pointer"
          >
            This Weekend
          </button>
          <button
            type="button"
            onClick={() => applyPreset('nextMonth')}
            className="px-2.5 py-1 text-xs rounded-full border border-primary/20 bg-primary/5 hover:bg-primary/15 text-foreground transition-all cursor-pointer"
          >
            Next Month
          </button>
          <button
            type="button"
            onClick={() => applyPreset('in3Months')}
            className="px-2.5 py-1 text-xs rounded-full border border-primary/20 bg-primary/5 hover:bg-primary/15 text-foreground transition-all cursor-pointer"
          >
            In 3 Months
          </button>
          <button
            type="button"
            onClick={() => applyPreset('in6Months')}
            className="px-2.5 py-1 text-xs rounded-full border border-primary/20 bg-primary/5 hover:bg-primary/15 text-foreground transition-all cursor-pointer"
          >
            In 6 Months
          </button>
          <button
            type="button"
            onClick={() => applyPreset('nextYear')}
            className="px-2.5 py-1 text-xs rounded-full border border-primary/20 bg-primary/5 hover:bg-primary/15 text-foreground transition-all cursor-pointer"
          >
            Next Year
          </button>
        </div>

        {/* Month & Year Navigation */}
        <div className="px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <select
              value={viewMonth}
              onChange={(e) => setViewMonth(parseInt(e.target.value, 10))}
              aria-label="Select month"
              className="text-sm font-semibold bg-transparent border border-border/80 rounded-lg px-2 py-1 focus:ring-1 focus:ring-primary focus:outline-hidden cursor-pointer"
            >
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={index}>
                  {name}
                </option>
              ))}
            </select>
            <select
              value={viewYear}
              onChange={(e) => setViewYear(parseInt(e.target.value, 10))}
              aria-label="Select year"
              className="text-sm font-semibold bg-transparent border border-border/80 rounded-lg px-2 py-1 focus:ring-1 focus:ring-primary focus:outline-hidden cursor-pointer"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={goToPreviousMonth}
              aria-label="Previous month"
              className="p-1.5 rounded-lg border border-border/70 hover:bg-muted text-foreground transition-colors cursor-pointer"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={goToNextMonth}
              aria-label="Next month"
              className="p-1.5 rounded-lg border border-border/70 hover:bg-muted text-foreground transition-colors cursor-pointer"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="px-5 pb-3">
          <div className="grid grid-cols-7 gap-1 text-center mb-1">
            {DAYS_OF_WEEK.map((d) => (
              <div key={d} className="text-[11px] font-semibold text-muted-foreground py-1">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDayOfWeek }).map((_, i) => (
              <div key={`empty-${i}`} className="h-9 w-full" />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateISO = formatDateToISO(viewYear, viewMonth, day);
              const isToday = dateISO === todayISO;
              const isPast = dateISO < todayISO;
              const isStart = dateISO === selectedStartDate;
              const isEnd = dateISO === selectedEndDate;
              const isInRange =
                isMultiDay &&
                selectedStartDate &&
                selectedEndDate &&
                dateISO > selectedStartDate &&
                dateISO < selectedEndDate;

              let cellStyle =
                'h-9 w-full rounded-lg text-xs font-medium transition-all flex items-center justify-center cursor-pointer ';

              if (isStart || isEnd) {
                cellStyle += 'bg-primary text-primary-foreground font-semibold shadow-xs';
              } else if (isInRange) {
                cellStyle += 'bg-primary/20 text-primary font-medium';
              } else if (isToday) {
                cellStyle += 'border border-primary text-primary font-semibold hover:bg-primary/10';
              } else if (isPast) {
                cellStyle += 'text-muted-foreground/50 hover:bg-muted/40';
              } else {
                cellStyle += 'text-foreground hover:bg-muted hover:text-foreground';
              }

              return (
                <button
                  key={`day-${day}`}
                  type="button"
                  onClick={() => handleDateClick(day)}
                  className={cellStyle}
                  aria-label={`${MONTH_NAMES[viewMonth]} ${day}, ${viewYear}`}
                  aria-pressed={isStart || isEnd}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>

        {/* Options (Multi-day & Venue TBD) */}
        <div className="px-5 py-2.5 border-t border-border/70 bg-muted/20 space-y-2 text-xs">
          <label className="flex items-center gap-2 cursor-pointer select-none text-foreground">
            <input
              type="checkbox"
              checked={isMultiDay}
              onChange={(e) => {
                setIsMultiDay(e.target.checked);
                if (!e.target.checked) setSelectedEndDate(undefined);
              }}
              className="rounded border-border text-primary focus:ring-primary h-3.5 w-3.5"
            />
            <span>Multi-day celebration (select start & end date)</span>
          </label>

          {showVenueTbdOption && (
            <label className="flex items-center gap-2 cursor-pointer select-none text-foreground">
              <input
                type="checkbox"
                checked={isVenueTbd}
                onChange={(e) => setIsVenueTbd(e.target.checked)}
                className="rounded border-border text-primary focus:ring-primary h-3.5 w-3.5"
              />
              <span>Venue is still TBD</span>
            </label>
          )}
        </div>

        {/* Selected date preview & actions */}
        <div className="p-4 border-t border-border/80 bg-muted/40 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <span className="text-[11px] text-muted-foreground block font-medium">Selected Date:</span>
            <span className="text-xs font-semibold text-foreground truncate block">
              {selectedStartDate
                ? isMultiDay && selectedEndDate && selectedEndDate !== selectedStartDate
                  ? `${formatDateDisplay(selectedStartDate)} → ${formatDateDisplay(selectedEndDate)}`
                  : formatDateDisplay(selectedStartDate)
                : 'No date selected'}
              {isVenueTbd && ' (Venue TBD)'}
            </span>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleConfirm}
              disabled={!selectedStartDate}
              className="gap-1.5 bg-primary text-primary-foreground font-semibold"
            >
              <Check className="h-3.5 w-3.5" />
              Confirm Date
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
